import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import { FinanceType } from '../../../services/dtos/finances.dto';
import { RelationType } from '../../../services/dtos/relation.dto';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import React, { useEffect, useState } from 'react';
import { Grid, Skeleton } from '@mui/material';
import { FileDto } from '../../../services/dtos/file.dto';
import OpenFile from '../../action/open-file';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import { PricesDto } from '../../../services/dtos/prices.dto';
import NoData from '../../commun/no-data';
import { getPriceDocument } from '../../../services/relation.service';
import { AxiosError } from 'axios';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import StyledTable from '../../commun/table';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';

export default function PriceUi(
    props: Readonly<{
        mandate?: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
        type: FinanceType | RelationType;
        handlerSetType: (newFinance: FinanceType | RelationType | undefined, text: string | undefined) => void;
    }>
) {
    // loader du tableau
    const [loading, setLoading] = useState(false);
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    const [openedFile, setOpenedFile] = useState<FileDto>();
    const [priceDocuments, setPriceDocuments] = useState<ResultSearchDto<PricesDto>>();
    const [page, setPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    // Création des cellules têtes du tableau
    // Les ids des cellules doivent correspondre aux noms des champs DTO, sauf l'action
    const headCells: HeadCellDto<PricesDto, keyof PricesDto>[] = [
        { id: 'designation', label: 'Intitulé', sort: false, width: 50 },
        { id: 'establishment', label: 'Etablissement', sort: false, width: 50 },
    ];

    useEffect(() => {
        setOpenedFile(undefined);
        if (props.mandate) {
            setLoading(true);
            getPriceDocument(props.mandate?.id, page, itemsPerPage)
                .then(result => {
                    setPriceDocuments(result);
                })
                .catch((error: Error | AxiosError) => {
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setLoading(false);
                });
        }

        return () => {
            setOpenedFile(undefined);
        };
    }, [itemsPerPage, page, props.mandate, showMessage]);

    //Action à mettre dans le tableau
    const action = (item: PricesDto) => {
        if (item.document?.file && item.document?.fileName) {
            const document: FileDto = {
                name: item.document.fileName,
                data: item.document.file,
            };
            return (
                <OpenFile handlerOpenFile={handlerOpenFile} handlerDownloadFile={handlerDownloadFile} file={document} />
            );
        }
        return <></>;
    };

    // Permet de télécharger un document de la réunion
    const handlerDownloadFile = async (paramFile: FileDto) => {
        if (paramFile.content) {
            const fileURL = globalThis.URL.createObjectURL(paramFile.content);
            globalThis.open(fileURL);
            return;
        }

        try {
            const response = await fetch(paramFile.data);
            const content = await response.blob();
            const fileURL = globalThis.URL.createObjectURL(content);
            globalThis.open(fileURL);
        } catch (error) {
            showAxiosMessageError(error as Error | AxiosError, showMessage);
        }
    };

    // Permet de visualiser un document de la réunion
    const handlerOpenFile = (paramFile: FileDto) => {
        setOpenedFile(paramFile);
    };

    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeItemsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setItemsPerPage(Number.parseInt(event.target.value, 10));
        setPage(0);
    };

    function displayPriceDocuments() {
        let tableElement;
        if (loading) {
            tableElement = <Skeleton variant={'rectangular'} height={150} />;
        } else if (!priceDocuments || priceDocuments.items.length === 0) {
            tableElement = <NoData title={'Information : '} content={'Aucun document'} />;
        } else {
            tableElement = (
                <StyledTable
                    data={priceDocuments.items}
                    searching={false}
                    action={action}
                    headCells={headCells}
                    totalItems={priceDocuments.totalItems}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeItemsPerPage}
                    itemsPerPage={itemsPerPage}
                ></StyledTable>
            );
        }

        return tableElement;
    }

    //Retourne la vue
    return (
        <Grid container className={'relation-request-content relation-price-content'} spacing={2}>
            <Grid item xs={12} md={6}>
                {displayPriceDocuments()}
            </Grid>
            {/*On affiche le document*/}
            <Grid item xs={12} md={6} className="relation-price-content__preview">
                {openedFile !== undefined && (
                    // eslint-disable-next-line jsx-a11y/alt-text
                    <object
                        data-testid={'pdf'}
                        className={'pdf'}
                        style={{ height: 'inherit', width: 'inherit' }}
                        data={openedFile.data}
                    ></object>
                )}
            </Grid>
        </Grid>
    );
}
